import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Avatar,
  Chip,
  Slider
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  Handshake
} from '@mui/icons-material';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

interface Partner {
  id: string;
  name: string;
  logoBase64: string;
  isActive: boolean;
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

const PartnersManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newPartnerForm, setNewPartnerForm] = useState({
    name: '',
    logoFile: null as File | null,
    logoPreview: '',
    isActive: true
  });

  const [editForm, setEditForm] = useState({
    name: '',
    logoFile: null as File | null,
    logoPreview: '',
    isActive: true
  });

  // Cropper states
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/admin/partners', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setPartners(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching partners:', error);
      setError('İş ortakları listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      setError('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setIsEditMode(isEdit);
      setCropDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels || !imageToCrop) return;

    const image = new Image();
    image.src = imageToCrop;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Create square canvas 300x300
    canvas.width = 300;
    canvas.height = 300;

    // Draw cropped image
    ctx?.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      300,
      300
    );

    // Convert to base64
    const croppedBase64 = canvas.toDataURL('image/png', 0.9);

    if (isEditMode) {
      setEditForm(prev => ({
        ...prev,
        logoPreview: croppedBase64
      }));
    } else {
      setNewPartnerForm(prev => ({
        ...prev,
        logoPreview: croppedBase64
      }));
    }

    setCropDialogOpen(false);
  };

  const handleCreatePartner = async () => {
    try {
      if (!newPartnerForm.name || !newPartnerForm.logoPreview) {
        setError('Partner adı ve logo zorunludur');
        return;
      }

      const token = localStorage.getItem('adminToken');
      const response = await axios.post('https://api.sobcof.com/api/admin/partners', {
        name: newPartnerForm.name,
        logoBase64: newPartnerForm.logoPreview,
        isActive: newPartnerForm.isActive
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('İş ortağı başarıyla eklendi');
        setCreateDialogOpen(false);
        setNewPartnerForm({
          name: '',
          logoFile: null,
          logoPreview: '',
          isActive: true
        });
        fetchPartners();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'İş ortağı eklenemedi');
    }
  };

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setEditForm({
      name: partner.name,
      logoFile: null,
      logoPreview: partner.logoBase64,
      isActive: partner.isActive
    });
    setEditDialogOpen(true);
  };

  const handleUpdatePartner = async () => {
    if (!selectedPartner) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`https://api.sobcof.com/api/admin/partners/${selectedPartner.id}`, {
        name: editForm.name,
        logoBase64: editForm.logoPreview,
        isActive: editForm.isActive
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('İş ortağı başarıyla güncellendi');
      setEditDialogOpen(false);
      fetchPartners();
    } catch (error: any) {
      setError(error.response?.data?.message || 'İş ortağı güncellenemedi');
    }
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`https://api.sobcof.com/api/admin/partners/${selectedPartner.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('İş ortağı başarıyla silindi');
      setDeleteDialogOpen(false);
      setSelectedPartner(null);
      fetchPartners();
    } catch (error: any) {
      setError(error.response?.data?.message || 'İş ortağı silinemedi');
    }
  };

  const movePartner = async (partnerId: string, direction: 'up' | 'down') => {
    const currentIndex = partners.findIndex(p => p.id === partnerId);
    if (currentIndex === -1) return;

    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === partners.length - 1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const updatedPartners = [...partners];

    // Swap positions
    [updatedPartners[currentIndex], updatedPartners[newIndex]] =
    [updatedPartners[newIndex], updatedPartners[currentIndex]];

    // Update order numbers
    const reorderedPartners = updatedPartners.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setPartners(reorderedPartners);

    // Save to backend
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post('https://api.sobcof.com/api/admin/partners/reorder', {
        partners: reorderedPartners.map(item => ({ id: item.id, order: item.order }))
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Sıralama başarıyla güncellendi');
      setTimeout(() => setSuccess(''), 2000);
    } catch (error: any) {
      setError('Sıralama güncellenemedi');
      fetchPartners(); // Revert on error
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#8B4513' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Handshake sx={{ ml: 2, mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            İş Ortakları Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ backgroundColor: '#fff', color: '#8B4513', '&:hover': { backgroundColor: '#f0f0f0' } }}
          >
            Yeni İş Ortağı
          </Button>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Card>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Handshake /> İş Ortakları Listesi
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Yukarı/aşağı butonları ile sıralama yapabilirsiniz
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="100">Sıralama</TableCell>
                  <TableCell>Logo</TableCell>
                  <TableCell>Partner Adı</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Sıra</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.map((partner, index) => (
                  <TableRow key={partner.id} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => movePartner(partner.id, 'up')}
                          disabled={index === 0}
                          color="primary"
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => movePartner(partner.id, 'down')}
                          disabled={index === partners.length - 1}
                          color="primary"
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Avatar
                        src={partner.logoBase64}
                        alt={partner.name}
                        sx={{ width: 60, height: 60, border: '2px solid #e0e0e0' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {partner.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={partner.isActive ? 'Aktif' : 'Pasif'}
                        color={partner.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={partner.order} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleEditPartner(partner)}
                        color="primary"
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedPartner(partner);
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {partners.length === 0 && !loading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Henüz iş ortağı eklenmemiş
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni İş Ortağı Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Partner Adı"
              value={newPartnerForm.name}
              onChange={(e) => setNewPartnerForm({ ...newPartnerForm, name: e.target.value })}
              fullWidth
              required
            />

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                Logo Yükle (Yuvarlak, 300x300px)
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleLogoUpload(e, false)}
                />
              </Button>
              {newPartnerForm.logoPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Avatar
                    src={newPartnerForm.logoPreview}
                    alt="Preview"
                    sx={{ width: 120, height: 120, margin: '0 auto', border: '2px solid #e0e0e0' }}
                  />
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={newPartnerForm.isActive}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, isActive: e.target.checked })}
                />
              }
              label="Aktif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>İptal</Button>
          <Button onClick={handleCreatePartner} variant="contained">Ekle</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>İş Ortağı Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Partner Adı"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
              required
            />

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                Yeni Logo Yükle
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleLogoUpload(e, true)}
                />
              </Button>
              {editForm.logoPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Avatar
                    src={editForm.logoPreview}
                    alt="Preview"
                    sx={{ width: 120, height: 120, margin: '0 auto', border: '2px solid #e0e0e0' }}
                  />
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
              }
              label="Aktif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleUpdatePartner} variant="contained">Güncelle</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>İş Ortağı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedPartner?.name} adlı iş ortağını silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeletePartner} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Logo Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', height: 400, backgroundColor: '#000', mt: 2 }}>
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
          <Box sx={{ mt: 3, px: 2 }}>
            <Typography variant="body2" gutterBottom>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, value) => setZoom(value as number)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropDialogOpen(false)}>İptal</Button>
          <Button onClick={createCroppedImage} variant="contained">Tamam</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnersManagementPage;
